/* global globalThis */
/**
 * Manifeste d'un dossier de preuve : chaque fichier y figure avec son
 * empreinte SHA-256. Recalculer l'empreinte d'un fichier reçu
 * (`sha256sum fichier`) et la comparer prouve qu'il n'a pas été modifié
 * depuis l'export.
 */

async function toBytes(content) {
    // Encodeur créé à l'usage : certains environnements (jsdom) ne l'ont pas au chargement.
    if (typeof content === 'string') return new TextEncoder().encode(content);
    if (content instanceof Uint8Array) return content;
    if (content instanceof ArrayBuffer) return new Uint8Array(content);
    if (content && typeof content.arrayBuffer === 'function') return new Uint8Array(await content.arrayBuffer());
    throw new Error('Contenu de fichier non pris en charge');
}

export async function sha256Hex(content) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', await toBytes(content));
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * @param files [{ path, content, description }]
 * @returns { manifest, manifestJson, entries } — entries garde le contenu en octets.
 */
export async function buildManifest({ bundle, files, generatedAt = Date.now(), source = '' }) {
    const entries = [];
    for (const file of files) {
        const bytes = await toBytes(file.content);
        entries.push({ path: file.path, bytes, description: file.description || '', sha256: await sha256Hex(bytes) });
    }
    const manifest = {
        format: 'monalerte-dossier-preuve',
        version: 1,
        activite: {
            id: bundle?.id || null,
            type: bundle?.kind || null,
            titre: bundle?.title || null,
            debut: bundle?.startMs ? new Date(bundle.startMs).toISOString() : null,
            fin: bundle?.endMs ? new Date(bundle.endMs).toISOString() : null,
            participants: (bundle?.tracks || []).map(track => ({ id: track.userId, nom: track.label, role: track.role })),
        },
        genere_le: new Date(generatedAt).toISOString(),
        source,
        algorithme_empreinte: 'SHA-256',
        fichiers: entries.map(entry => ({
            chemin: entry.path,
            octets: entry.bytes.byteLength,
            sha256: entry.sha256,
            description: entry.description,
        })),
    };
    const manifestJson = `${JSON.stringify(manifest, null, 2)}\n`;
    return { manifest, manifestJson, entries, manifestSha256: await sha256Hex(manifestJson) };
}

/** Fichier lisible par un humain : comment vérifier le dossier sans MonAlerte. */
export function verificationInstructions(manifest, manifestSha256) {
    const lines = [
        'DOSSIER DE PREUVE MONALERTE — PROCÉDURE DE VÉRIFICATION',
        '',
        'Pour comprendre le dossier (contenu, usage de chaque fichier), lire d\'abord LISEZ-MOI.html.',
        '',
        `Activité : ${manifest.activite.titre || ''} (${manifest.activite.id || ''})`,
        `Période : ${manifest.activite.debut || '?'} → ${manifest.activite.fin || '?'}`,
        `Export généré le : ${manifest.genere_le}`,
        `Empreinte SHA-256 du manifeste : ${manifestSha256}`,
        '',
        '1. Vérifier le manifeste lui-même :',
        '     sha256sum manifest.json        (Linux)',
        '     shasum -a 256 manifest.json    (macOS)',
        '     certutil -hashfile manifest.json SHA256   (Windows)',
        '   Le résultat doit être identique à l\'empreinte ci-dessus, également',
        '   inscrite au journal d\'audit du serveur au moment de l\'export.',
        '',
        '2. Vérifier chaque fichier : recalculer son empreinte de la même façon et',
        '   la comparer à la valeur « sha256 » de manifest.json.',
        '',
        'Toute différence signifie que le fichier a été modifié après l\'export.',
        '',
        '3. Registre de preuve (preuve/registre.json) et horodatage : ouvrir',
        '   verify.html dans un navigateur et sélectionner le dossier ; il recalcule',
        '   les empreintes, la chaîne et les preuves de Merkle. Les jetons',
        '   preuve/ancrages/*.tsr se vérifient avec « openssl ts -verify ».',
        '',
        'Fichiers :',
        ...manifest.fichiers.map(file => `  ${file.sha256}  ${file.chemin}`),
        '',
    ];
    return lines.join('\n');
}
